import { Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { forcePasswordChangeGuard } from './core/auth/force-password-change.guard';
import { loginRedirectGuard } from './core/auth/login-redirect.guard';
import { adminGuard, adminOrDealerGuard, dealerGuard } from './core/auth/role.guards';
import { agreementTextUnsavedChangesGuard } from './features/agreement-text/agreement-text-unsaved.guard';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [loginRedirectGuard],
    loadComponent: () => import('./features/auth/login').then((m) => m.Login),
  },
  {
    path: 'auth/callback',
    loadComponent: () => import('./features/auth/oidc-callback').then((m) => m.OidcCallback),
  },
  {
    path: 'reset-password',
    canActivate: [loginRedirectGuard],
    loadComponent: () => import('./features/auth/reset-password').then((m) => m.ResetPassword),
  },
  {
    path: 'agreements/accept',
    loadComponent: () =>
      import('./features/agreements/agreement-accept').then((m) => m.AgreementAccept),
  },
  {
    path: 'agreements/:id/payment-setup',
    loadComponent: () => import('./features/agreements/payment-setup').then((m) => m.PaymentSetup),
  },
  {
    path: 'return',
    loadComponent: () =>
      import('./features/agreements/payment-return').then((m) => m.PaymentReturn),
  },
  {
    path: 'cancel',
    loadComponent: () =>
      import('./features/agreements/payment-cancel').then((m) => m.PaymentCancel),
  },
  {
    path: '',
    canActivate: [authGuard],
    canActivateChild: [forcePasswordChangeGuard],
    loadComponent: () => import('./core/layout/shell').then((m) => m.Shell),
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./core/layout/home-redirect').then((m) => m.HomeRedirect),
      },
      {
        path: 'change-password',
        canActivate: [dealerGuard],
        loadComponent: () =>
          import('./features/auth/change-password').then((m) => m.ChangePassword),
      },
      {
        path: 'dealers',
        canActivate: [adminGuard],
        loadComponent: () => import('./features/dealers/dealer-list').then((m) => m.DealerList),
      },
      {
        path: 'dealers/:id',
        canActivate: [adminGuard],
        loadComponent: () => import('./features/dealers/dealer-detail').then((m) => m.DealerDetail),
      },
      {
        path: 'agreement-types',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/agreement-types/agreement-type-list').then((m) => m.AgreementTypeList),
      },
      {
        path: 'agreement-types/:id',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/agreement-types/agreement-type-detail').then(
            (m) => m.AgreementTypeDetail,
          ),
      },
      {
        path: 'agreement-text',
        canActivate: [adminGuard],
        canDeactivate: [agreementTextUnsavedChangesGuard],
        loadComponent: () =>
          import('./features/agreement-text/agreement-text').then((m) => m.AgreementText),
      },
      {
        path: 'customers',
        canActivate: [adminOrDealerGuard],
        loadComponent: () =>
          import('./features/customers/customer-list').then((m) => m.CustomerList),
      },
      {
        path: 'customers/:id/payment-setup',
        canActivate: [adminOrDealerGuard],
        loadComponent: () =>
          import('./features/customers/customer-payment-setup').then((m) => m.CustomerPaymentSetup),
      },
      {
        path: 'customers/:id',
        canActivate: [adminOrDealerGuard],
        loadComponent: () =>
          import('./features/customers/customer-detail').then((m) => m.CustomerDetail),
      },
      {
        path: 'agreements',
        canActivate: [adminOrDealerGuard],
        loadComponent: () =>
          import('./features/agreements/agreement-list').then((m) => m.AgreementList),
      },
      {
        path: 'agreements/compare',
        canActivate: [adminOrDealerGuard],
        loadComponent: () =>
          import('./features/agreements/compare/agreement-compare').then((m) => m.AgreementCompare),
      },
      {
        path: 'agreements/:id/admin-payment-setup',
        canActivate: [adminOrDealerGuard],
        loadComponent: () =>
          import('./features/agreements/payment-setup').then((m) => m.PaymentSetup),
        data: { internalPaymentSetup: true },
      },
      {
        path: 'agreements/:id',
        canActivate: [adminOrDealerGuard],
        loadComponent: () =>
          import('./features/agreements/agreement-detail').then((m) => m.AgreementDetail),
      },
      {
        path: 'pricing-items',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/pricing-items/pricing-item-list').then((m) => m.PricingItemList),
      },
      {
        path: 'audit',
        canActivate: [adminGuard],
        loadComponent: () => import('./features/audit/audit-log').then((m) => m.AuditLog),
      },
      {
        path: 'audit/:id',
        canActivate: [adminGuard],
        loadComponent: () => import('./features/audit/audit-detail').then((m) => m.AuditDetail),
      },
      {
        path: 'payment-settlements',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/payment-settlements/payment-settlement-list').then(
            (m) => m.PaymentSettlementList,
          ),
      },
      {
        path: 'payment-settlements/:paymentMethod/:merchantId',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/payment-settlements/payment-settlement-detail').then(
            (m) => m.PaymentSettlementDetail,
          ),
      },
      {
        path: 'agreement-collection',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/agreement-collection/agreement-collection').then(
            (m) => m.AgreementCollection,
          ),
      },
      {
        path: 'betalingsservice-files',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/betalingsservice-files/betalingsservice-files').then(
            (m) => m.BetalingsserviceFiles,
          ),
      },
      {
        path: 'error-reports',
        canActivate: [adminGuard],
        loadChildren: () =>
          import('./features/error-reports/error-reports.routes').then((m) => m.errorReportsRoutes),
      },
      {
        path: 'reports',
        canActivate: [adminGuard],
        loadComponent: () => import('./features/reports/reports').then((m) => m.Reports),
      },
      {
        path: 'access-denied',
        loadComponent: () => import('./features/errors/access-denied').then((m) => m.AccessDenied),
      },
      {
        path: '**',
        loadComponent: () => import('./features/errors/not-found').then((m) => m.NotFound),
      },
    ],
  },
  { path: '**', redirectTo: '/login' },
];
